import { useState, useEffect } from 'react';
import { productQuestionAPI } from '../../services/api';
import { HelpCircle, MessageSquare, Trash2, Send, ChevronLeft, ChevronRight } from 'lucide-react';

export default function QuestionsAdmin() {
    const [questions, setQuestions] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [filter, setFilter] = useState('all');
    const [loading, setLoading] = useState(false);
    const [answerMap, setAnswerMap] = useState({});

    const load = async () => {
        setLoading(true);
        try {
            const params = { page, limit: 20 };
            if (filter !== 'all') params.status = filter;
            const res = await productQuestionAPI.adminGetAll(params);
            setQuestions(res.data.questions || []);
            setTotal(res.data.total || 0);
            setPages(res.data.pages || 1);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    useEffect(() => { load(); }, [page, filter]);

    const handleAnswer = async (id) => {
        const answer = answerMap[id]?.trim();
        if (!answer) return;
        try {
            await productQuestionAPI.adminAnswer(id, { answer });
            setAnswerMap(prev => { const copy = { ...prev }; delete copy[id]; return copy; });
            load();
        } catch (e) { console.error(e); }
    };

    const handleDelete = async (id) => {
        if (!confirm('Bu soruyu silmek istediğinize emin misiniz?')) return;
        try {
            await productQuestionAPI.adminDelete(id);
            load();
        } catch (e) { console.error(e); }
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <h2 style={{ margin: 0 }}>Ürün Soruları ({total})</h2>
                <div className="review-admin-filters">
                    {['all', 'pending', 'answered'].map(f => (
                        <button key={f} className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-secondary'}`} onClick={() => { setFilter(f); setPage(1); }}>
                            {f === 'all' ? 'Tümü' : f === 'pending' ? 'Cevaplanmamış' : 'Cevaplanmış'}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="loading"><div className="spinner"></div></div>
            ) : questions.length === 0 ? (
                <div className="empty-state">
                    <HelpCircle size={48} strokeWidth={1} />
                    <h3>Soru bulunamadı</h3>
                </div>
            ) : (
                <div className="review-admin-list">
                    {questions.map(q => (
                        <div key={q.id} className={`review-admin-card ${q.isAnswered ? 'approved' : ''}`}>
                            <div className="review-admin-top">
                                <div className="review-admin-meta">
                                    <strong>{q.customer?.firstName} {q.customer?.lastName}</strong>
                                    <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{q.customer?.email}</span>
                                </div>
                                <span className="review-admin-status" style={{ background: q.isAnswered ? 'var(--success)' : '#f39c12' }}>
                                    {q.isAnswered ? 'Cevaplanmış' : 'Bekliyor'}
                                </span>
                            </div>
                            <div className="review-admin-product">
                                <MessageSquare size={14} /> {q.product?.name}
                            </div>
                            <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid var(--border)' }}>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: q.answer ? '0.75rem' : 0 }}>
                                    <span style={{ background: 'var(--accent-primary)', color: '#fff', width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 800, flexShrink: 0 }}>S</span>
                                    <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-primary)' }}>{q.question}</p>
                                </div>
                                {q.answer && (
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                                        <span style={{ background: 'var(--success)', color: '#fff', width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 800, flexShrink: 0 }}>C</span>
                                        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-primary)' }}>{q.answer}</p>
                                    </div>
                                )}
                            </div>
                            {!q.isAnswered && (
                                <div style={{ padding: '0 1rem 1rem', display: 'flex', gap: '0.5rem' }}>
                                    <input
                                        type="text"
                                        placeholder="Cevabınızı yazın..."
                                        value={answerMap[q.id] || ''}
                                        onChange={e => setAnswerMap(prev => ({ ...prev, [q.id]: e.target.value }))}
                                        onKeyDown={e => { if (e.key === 'Enter') handleAnswer(q.id); }}
                                        style={{ flex: 1, padding: '0.5rem 0.75rem', border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--bg-input, var(--bg-secondary))', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                                    />
                                    <button className="btn btn-primary btn-sm" onClick={() => handleAnswer(q.id)} disabled={!answerMap[q.id]?.trim()}>
                                        <Send size={14} /> Cevapla
                                    </button>
                                </div>
                            )}
                            <div className="review-admin-actions" style={{ borderTop: '1px solid var(--border)', padding: '0.5rem 1rem' }}>
                                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                                    {new Date(q.createdAt).toLocaleDateString('tr-TR')}
                                </span>
                                <button className="btn btn-sm" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(q.id)}>
                                    <Trash2 size={14} /> Sil
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {pages > 1 && (
                <div className="pagination" style={{ marginTop: '1.5rem' }}>
                    <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                        <ChevronLeft size={16} /> Önceki
                    </button>
                    <span style={{ padding: '0 1rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                        {page} / {pages}
                    </span>
                    <button disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
                        Sonraki <ChevronRight size={16} />
                    </button>
                </div>
            )}
        </div>
    );
}
